import { makeObservable, computed, observable, runInAction } from 'mobx';
import globalNav from '~/lib/store/GlobalNavStore';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ModalBody, NavItem, NavLink, Spinner } from 'reactstrap';
import DialogModal from '~/components/DialogModal';
import HtmlElementInstance from '~/components/HtmlElementInstance';
import ExternalLink from '~/components/ExternalLink';

import './Sidebar.scss';
import { getAppContext } from '~/lib/AppContext';
import { DarkModeToggle } from './DarkModeToggle';
import SupportCodeNav from './SupportCodeNav';
import { logoutZendeskMessenger } from '~/lib/helper/ZendeskHelper';
import { hasSegments, SupportRoute } from '~/lib/helper/RouteHelper';

type SidebarProps = {
    location: Location;
    children?: React.ReactNode; // Main navigation content
    forSignup?: boolean; // Whether to show search or feedback in profile nav
};

const feedbackSegments = ['/feedback'];

// This markup is mirrored by the pre-mount skeleton in
// Views/Shared/PanelPage.cshtml — keep both in sync.
@observer
class Sidebar extends React.Component<SidebarProps> {
    @observable isLoggingOut = false;

    constructor(props: SidebarProps) {
        super(props);
        makeObservable(this);
    }

    @computed
    get currentSubNav() {
        return globalNav.navStack.length > 0 ? globalNav.navStack[Math.max(0, globalNav.navStack.length - 1)] : null;
    }

    toggle = () => globalNav.showNav();

    handleLogout = async () => {
        runInAction(() => {
            this.isLoggingOut = true;
        });
        logoutZendeskMessenger();
        await getAppContext()
            .siteApiClient.logout()
            .catch((e) => {
                console.error('Logout failed:', e);
            });
        window.location.href = getAppContext().config.wwwSite;
    };

    componentDidMount() {
        //find our toggler
        const el = document.getElementById('toggle-sidenav');
        el?.addEventListener('click', this.toggle);
    }

    componentWillUnmount() {
        const el = document.getElementById('toggle-sidenav');
        el?.removeEventListener('click', this.toggle);
    }

    render() {
        const appConfig = getAppContext().config;

        return (
            <div className={`sidebar ${this.currentSubNav ? 'withSubNav' : ''}`}>
                <div className={`bl-sidebar-links ${globalNav.showGlobalNav ? 'show' : ''}`}>
                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop overlay; nav toggle button provides keyboard access */}
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay; nav toggle button provides keyboard access */}
                    <div className="sideBarWrapper" onClick={() => globalNav.hideNav()}>
                        <div className="sideBarGlobalNav">
                            <ul className="nav logo">
                                <li>
                                    <NavLink href={appConfig.wwwSite} title={appConfig.brandName}>
                                        <img
                                            src={`/img/icon-logo-${appConfig.theme.toLowerCase()}.png`}
                                            width="38"
                                            className="logo-icon"
                                            alt={`${appConfig.brandName} icon`}
                                        />
                                        <img
                                            src={`/img/logo-${appConfig.theme.toLowerCase()}.png`}
                                            width="125"
                                            className="logo-horizontal"
                                            alt={`${appConfig.brandName} logo`}
                                        />
                                    </NavLink>
                                </li>
                            </ul>

                            {this.props.children}

                            <ul className="nav profileNav">
                                {!this.props.forSignup && (
                                    <>
                                        <NavItem>
                                            <NavLink
                                                title="Search"
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    getAppContext().showSearch.fire(true);
                                                }}
                                            >
                                                <i className="icon ion-md-search" />
                                                <span className="label">Search</span>
                                            </NavLink>
                                        </NavItem>
                                        <NavItem
                                            className={
                                                hasSegments(this.props.location.pathname, feedbackSegments)
                                                    ? 'active'
                                                    : ''
                                            }
                                        >
                                            <NavLink href="/feedback" title="Feedback">
                                                <i className="icon ion-md-chatboxes" />
                                                <span className="label">Feedback</span>
                                            </NavLink>
                                        </NavItem>
                                    </>
                                )}
                                <SupportCodeNav store={getAppContext().supportCode} />
                                <NavItem>
                                    <ExternalLink tag={NavLink} href={SupportRoute} title="Help Desk" showIcon>
                                        <i className="icon ion-md-help" />
                                        <span className="label">Help Desk</span>
                                    </ExternalLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        title="Log out"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            void this.handleLogout();
                                        }}
                                    >
                                        <i className="icon ion-md-exit" />
                                        <span className="label">Log out</span>
                                    </NavLink>
                                </NavItem>
                            </ul>
                            <DarkModeToggle />
                        </div>
                        {this.currentSubNav && (
                            <HtmlElementInstance className="sideBarSubNav" el={this.currentSubNav} />
                        )}
                    </div>
                </div>
                {this.isLoggingOut && (
                    <DialogModal isOpen={true} centered labelledBy="signing-out-title">
                        <ModalBody>
                            <div className="d-flex flex-column align-items-center">
                                <Spinner />
                                <div id="signing-out-title" className="mt-3">
                                    Logging out...
                                </div>
                            </div>
                        </ModalBody>
                    </DialogModal>
                )}
            </div>
        );
    }
}

export default Sidebar;
